import type { CSSProperties, FC, SVGProps } from 'react';
import config from 'config';
import Image, { type StaticImageData } from 'next/image';

import { Box, useBreakpointValue } from '@chakra-ui/react';

import { Link } from 'ui';

type SvgComponent = FC<SVGProps<SVGSVGElement>>;

const WIDE_LOGO_ASPECT = 730 / 150;

const isStaticImage = (value: unknown): value is StaticImageData =>
  typeof value === 'object' &&
  value !== null &&
  'src' in value &&
  typeof (value as StaticImageData).src === 'string' &&
  (value as StaticImageData).src.length > 0;

const isImageUrl = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isSvgComponent = (value: unknown): value is SvgComponent =>
  typeof value === 'function';

const getRasterSrc = (value: unknown): string | null => {
  if (isStaticImage(value)) return value.src;
  if (isImageUrl(value)) return value;
  return null;
};

const DEFAULT_LOGO_HEIGHT = { base: '46px', md: '75px' };
const DEFAULT_SVG_MAX_WIDTH = {
  base: 'min(100%, 320px)',
  md: '420px'
};

const Logo = () => {
  const {
    iconLogo: IconLogo,
    logo: logoAsset,
    logoLink,
    isExternalLink,
    isSquareIcon,
    isFullColorLogo,
    logoSize
  } = config;

  const logoHeight =
    useBreakpointValue(logoSize?.height ?? DEFAULT_LOGO_HEIGHT) ?? '46px';
  const svgMaxWidth = useBreakpointValue(
    logoSize?.maxWidth ?? DEFAULT_SVG_MAX_WIDTH
  );
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? true;
  const heightPx = parseFloat(logoHeight) || 46;

  const rasterSrc = isFullColorLogo ? getRasterSrc(logoAsset) : null;

  const logoLinkProps = {
    to: logoLink,
    external: isExternalLink,
    display: 'flex' as const,
    justifyContent: { base: 'center', md: 'flex-start' } as const,
    alignItems: 'center' as const,
    w: { base: '100%', md: 'auto' } as const,
    maxW: '100%' as const,
    sx: {
      lineHeight: 0,
      flexShrink: 0,
      minW: 0,
      '& > svg': {
        width: 'auto',
        height: 'auto',
        maxWidth: '100%'
      }
    }
  };

  const rasterObjectPosition = isMobile ? 'center' : 'left center';

  if (rasterSrc) {
    const isWideRaster = !isSquareIcon;

    const rasterImgStyle: CSSProperties = isSquareIcon
      ? {
          display: 'block',
          height: logoHeight,
          width: logoHeight,
          maxWidth: logoHeight,
          objectFit: 'contain',
          objectPosition: rasterObjectPosition
        }
      : {
          display: 'block',
          height: logoHeight,
          width: 'auto',
          maxWidth: '100%',
          objectFit: 'contain',
          objectPosition: rasterObjectPosition
        };

    const wideRasterSx = {
      display: 'block',
      width: { base: '100%', md: 'auto' },
      height: { base: 'auto', md: logoHeight },
      maxHeight: {
        base: `${Math.round(heightPx * 0.95)}px`,
        sm: `${heightPx}px`,
        md: logoHeight
      },
      maxWidth: '100%',
      objectFit: 'contain',
      objectPosition: rasterObjectPosition
    };

    if (isStaticImage(logoAsset)) {
      const aspect = logoAsset.width / logoAsset.height || WIDE_LOGO_ASPECT;
      const displayWidth = Math.round(heightPx * aspect);

      return (
        <Link {...logoLinkProps}>
          <Image
            src={logoAsset}
            alt=''
            width={displayWidth}
            height={heightPx}
            sizes='(max-width: 768px) 100vw, 480px'
            style={
              isWideRaster
                ? {
                    ...rasterImgStyle,
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: '100%',
                    height: logoHeight
                  }
                : rasterImgStyle
            }
            priority
          />
        </Link>
      );
    }

    return (
      <Link {...logoLinkProps}>
        <Box
          as='img'
          src={rasterSrc}
          alt=''
          sx={isWideRaster ? wideRasterSx : rasterImgStyle}
        />
      </Link>
    );
  }

  const SvgLogo: SvgComponent = isSvgComponent(logoAsset)
    ? logoAsset
    : IconLogo;

  const svgStyle: CSSProperties = isSquareIcon
    ? {
        display: 'block',
        height: logoHeight,
        width: logoHeight,
        maxWidth: logoHeight
      }
    : {
        display: 'block',
        maxHeight: logoHeight,
        width: 'auto',
        maxWidth: svgMaxWidth
      };

  return (
    <Link {...logoLinkProps}>
      <Box display='inline-block' maxW='100%' lineHeight={0}>
        <SvgLogo
          style={svgStyle}
          preserveAspectRatio='xMinYMid meet'
          role='img'
          aria-label='Home'
        />
      </Box>
    </Link>
  );
};

export default Logo;
